/**
 * Translate a raw ingest-job error (the technical string stored in jobs.error)
 * into something humans can act on: a friendly explanation + fixes for the
 * account user, and a root-cause hint for admins/support. Pure + shared so the
 * web app, the admin app, and the API all categorize failures identically.
 */

export interface JobErrorExplanation {
  /** Stable machine code for the failure class. */
  category:
    | 'account_not_provisioned'
    | 'data_store_unreachable'
    | 'storage_quota_exceeded'
    | 'layout_mismatch'
    | 'no_carrier_config'
    | 'missing_record_identity'
    | 'file_validation'
    | 'file_missing'
    | 'schema_not_migrated'
    | 'server_misconfig'
    | 'unknown';
  /** Short, non-technical headline. */
  title: string;
  /** Friendly explanation shown to the account user. */
  userMessage: string;
  /** Concrete next steps for the user. */
  fixes: string[];
  /** Whether re-running could plausibly succeed (transient vs permanent). */
  retryable: boolean;
  /** Root cause + what to tell the user — for admins/support. */
  adminHint: string;
}

interface Rule {
  category: JobErrorExplanation['category'];
  test: RegExp;
  build: (raw: string) => Omit<JobErrorExplanation, 'category'>;
}

const RULES: Rule[] = [
  {
    // Shape-aware ingest blocked the file: its layout doesn't match any known
    // mapping for the carrier, and blind-applying the active config would have
    // mis-mapped real money data. Re-running the same file never helps.
    category: 'layout_mismatch',
    test: /layout does not match the configured mapping/i,
    build: () => ({
      title: 'Statement layout doesn’t match',
      userMessage:
        'This file’s column layout doesn’t match the mapping we have for this carrier, and no alternative mapping fit — so we didn’t ingest it, to protect your numbers from being mis-read.',
      fixes: [
        'Check you exported the right report from the carrier portal (a summary or activity export often has a different layout than the commission statement).',
        'If the carrier genuinely changed their format, upload a sample via “Set up a carrier” so a new mapping can be built, then re-upload.',
      ],
      retryable: false,
      adminHint:
        'Shape-aware ingest blocked a layout mismatch (see ingest.shape radar events for the dry-run map rates). Inspect the file headers vs the active config; if the new layout is legitimate, build/verify a mapping for it (signature library) and re-run the job.',
    }),
  },
  {
    category: 'account_not_provisioned',
    test: /no data-plane credentials|not provisioned|account data store is not provisioned/i,
    build: () => ({
      title: 'Account setup isn’t finished',
      userMessage:
        'Your account’s secure data store hasn’t been set up yet, so we couldn’t process your statement. Your file is fine — nothing is wrong with it.',
      fixes: [
        'No action needed on your end.',
        'Contact your CommissionSight rep so we can finish setting up your account, then re-upload.',
      ],
      retryable: false,
      adminHint:
        'Account has no row in account_db_credentials — its per-account database was never provisioned. Provision it (infra/provisioning) or store a connection string via Admin → Accounts → Details → credentials, then retry the job. Tell the user: this is a one-time account-setup step on our side; their file is valid.',
    }),
  },
  {
    category: 'data_store_unreachable',
    test: /econnrefused|etimedout|getaddrinfo|connection (refused|terminated|reset|timeout)|connect timeout|could not connect|password authentication failed|ssl|self[- ]signed|timed? ?out|unreachable/i,
    build: () => ({
      title: 'Couldn’t reach your data store',
      userMessage:
        'We had trouble reaching your account’s data store while processing your statement. This is usually temporary.',
      fixes: [
        'Wait a couple of minutes and re-upload (or use Retry).',
        'If it keeps happening, contact support.',
      ],
      retryable: true,
      adminHint:
        'Connection to the account’s database failed. Verify the stored connection string is valid and the database is awake/reachable from Cloudflare Workers (region, SSL, credentials). Once confirmed, retry the job.',
    }),
  },
  {
    // Neon enforces a per-project storage cap (e.g. 512 MB on the free tier);
    // a write that needs to extend a file then fails. Re-running never helps until
    // the project's storage is upgraded — so this is NON-retryable infra (don't
    // burn the queue's retries) and is surfaced CRITICAL for ops to act on.
    category: 'storage_quota_exceeded',
    test: /could not extend file|project size limit|storage (quota|limit)|disk (quota|full)|no space left|exceeded.*(size|storage|quota)/i,
    build: () => ({
      title: 'Temporary system issue',
      userMessage: 'We hit a temporary system issue while processing your file. Your file is fine.',
      fixes: ['No action needed — contact support; we’re expanding capacity, then re-upload.'],
      retryable: false,
      adminHint:
        'The account’s Postgres (Neon) project hit its STORAGE QUOTA ("could not extend file because project size limit … exceeded"). Re-running will keep failing. Upgrade the account’s Neon project storage (or move it to a larger plan), then re-upload the failed statements IN CHRONOLOGICAL ORDER. Watch per-account DB size — this account outgrew its tier.',
    }),
  },
  {
    category: 'no_carrier_config',
    test: /no active carrier config/i,
    build: () => ({
      title: 'This carrier’s format isn’t set up',
      userMessage:
        'We don’t have a statement format configured for this carrier yet, so we couldn’t read your file.',
      fixes: [
        'Double-check you selected the right carrier when uploading.',
        'Contact support to add this carrier’s statement format — then re-upload.',
      ],
      retryable: false,
      adminHint:
        'No active carrier_config (global or account override) exists for this carrier. Author one via Admin → Carriers → "Onboard from a sample file", then retry. Tell the user we’re adding their carrier’s format.',
    }),
  },
  {
    category: 'missing_record_identity',
    test: /no uniquely identifiable records|unique record id|missing a Member ID/i,
    build: () => ({
      title: 'No uniquely identifiable records',
      userMessage:
        'This file was rejected because its rows don’t carry a unique record identifier. Every member row must have a Member ID or a Policy ID so each record can be tracked.',
      fixes: [
        'Re-export the statement so each row includes a Member ID (or Policy ID).',
        'Check you selected the right carrier — its format expects an identifier column.',
      ],
      retryable: false,
      adminHint:
        'Ingest rejected the whole file: zero rows resolved a unique identity (member_external_id or policy_number). The carrier config must map an identifier column AND the file must populate it. A redacted/blank-ID export will trip this by design.',
    }),
  },
  {
    category: 'file_validation',
    test: /failed validation|missing required field|did not resolve|member_external_id|commission_amount|0 records ingested|mapped 0 of/i,
    build: (raw) => ({
      title: 'Some rows were missing required info',
      userMessage:
        'Some rows in your file were missing a member ID or a commission amount, so they couldn’t be scored.',
      fixes: [
        'Make sure every row has a member ID and a commission amount.',
        'Re-export the statement from the carrier and upload it again.',
      ],
      retryable: false,
      adminHint: `Rows failed required-field validation (${raw}). Most often the carrier config’s column mapping doesn’t match the file headers — confirm member_external_id and commission_amount map to the right columns for this carrier.`,
    }),
  },
  {
    category: 'file_missing',
    test: /r2 object .* not found|file .* not found/i,
    build: () => ({
      title: 'We lost track of your file',
      userMessage: 'We couldn’t find your uploaded file when it was time to process it.',
      fixes: ['Please re-upload the statement.'],
      retryable: false,
      adminHint:
        'The R2 object for this job’s key was missing. Re-upload creates a fresh file. Check the R2 bucket binding and key convention if this recurs.',
    }),
  },
  {
    category: 'schema_not_migrated',
    test: /relation .* does not exist|column .* does not exist|undefined table|no such table|does not exist/i,
    build: () => ({
      title: 'Account setup isn’t finished',
      userMessage:
        'Your account’s data store isn’t fully set up yet, so processing couldn’t complete. Your file is fine.',
      fixes: ['No action needed — contact support to finish setup, then re-upload.'],
      retryable: false,
      adminHint:
        'The account’s database is missing expected tables — the Postgres migrations weren’t applied during provisioning. Run the pg migrations against the account connection string, then retry.',
    }),
  },
  {
    category: 'server_misconfig',
    test: /encryption_key|secret is not configured/i,
    build: () => ({
      title: 'Temporary system issue',
      userMessage: 'We hit a temporary system issue while processing your file.',
      fixes: ['Try again shortly, or contact support.'],
      retryable: true,
      adminHint:
        'A required server secret (e.g. ENCRYPTION_KEY) is not configured on the API Worker. Set it via `wrangler secret put`, then retry.',
    }),
  },
];

export function explainJobError(raw: string | null | undefined): JobErrorExplanation | null {
  if (!raw) return null;
  for (const rule of RULES) {
    if (rule.test.test(raw)) return { category: rule.category, ...rule.build(raw) };
  }
  return {
    category: 'unknown',
    title: 'Processing didn’t complete',
    userMessage: 'Something went wrong while processing your statement.',
    fixes: [
      'Try uploading again.',
      'If it keeps failing, contact support and mention the time you uploaded.',
    ],
    retryable: true,
    adminHint: `Unrecognized error — raw message: ${raw}`,
  };
}
